import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { bookingServiceAPI, Booking } from '../services/bookingServiceAPI';
import { ticketService } from '../services/ticketService';
import { authService } from '../services/authService';
import RiyalSymbol from '../components/RiyalSymbol';
import QRCode from 'react-qr-code';
import '../styles/ConfirmedBookings.css';

function ConfirmedBookings() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [showScanDetails, setShowScanDetails] = useState<{booking: Booking, show: boolean} | null>(null);
  const [showPaymentQR, setShowPaymentQR] = useState<{booking: Booking, show: boolean} | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tickets, setTickets] = useState<any[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ticketsUsageCache, setTicketsUsageCache] = useState<{[key: string]: number}>({});

  useEffect(() => {
    loadBookings();
  }, []);

  useEffect(() => {
    if (bookings.length > 0) {
      loadTicketsUsageFromAPI();
    }
  }, [bookings]);

  const loadTicketsUsageFromAPI = async () => {
    // تحميل بيانات استخدام التذاكر من قاعدة البيانات
    try {
      const cacheData: {[key: string]: number} = {};
      
      // جلب بيانات استخدام التذاكر لكل حجز
      for (const booking of bookings) {
        const bookingId = booking.id || booking._id || '';
        if (bookingId && booking.bookingType === 'perPhoto') {
          try {
            // استخدام الـ bookingService الجديد لجلب التذاكر
            const { bookingService } = await import('../services/BookingService');
            const tickets = await bookingService.getTicketsByBooking(bookingId);
            const usedCount = tickets.filter((t: any) => t.used).length;
            cacheData[bookingId] = usedCount;
          } catch (error) {
            console.warn(`Failed to load tickets for booking ${bookingId}:`, error);
            cacheData[bookingId] = 0;
          }
        }
      }
      
      setTicketsUsageCache(cacheData);
      console.log('📊 تم تحميل بيانات استخدام التذاكر من قاعدة البيانات:', cacheData);
    } catch (error) {
      console.error('❌ خطأ في تحميل بيانات الاستخدام من API:', error);
    }
  };

  // Deprecated: تحميل البيانات من localStorage (للتوافق مع النسخة القديمة)
  const loadCachedUsageData = () => {
    console.warn('⚠️  loadCachedUsageData is deprecated - using API instead');
    // لا نستخدم localStorage بعد الآن
  };

  const loadBookings = async () => {
    try {
      setLoading(true);
      const confirmedBookings = await bookingServiceAPI.getConfirmedBookings();
      setBookings(confirmedBookings);
      setFilteredBookings(confirmedBookings);
    } catch (err: any) {
      setError(err.message || 'فشل جلب الحجوزات');
      console.error('Error loading bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = bookings;

    // تصفية حسب البحث
    if (searchTerm) {
      filtered = filtered.filter(booking => 
        booking.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.phone.includes(searchTerm) ||
        booking.bookingCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // تصفية حسب التاريخ
    if (filterDate) {
      filtered = filtered.filter(booking => 
        booking.eventDate === filterDate
      );
    }

    setFilteredBookings(filtered);
  }, [searchTerm, filterDate, bookings]);

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getBookingTypeLabel = (type: string) => {
    return type === 'hourly' ? 'نظام الساعات' : 'نظام التذاكر';
  };

  const getUsedTicketsCount = (booking: Booking) => {
    const bookingId = booking.id || booking._id || '';
    if (!bookingId) return 0;
    
    // تحقق من الكاش أولاً
    if (ticketsUsageCache[bookingId] !== undefined) {
      return ticketsUsageCache[bookingId];
    }
    
    // إذا لم توجد بيانات في الكاش، جلب من الـ API
    // هذا سيحصل في loadTicketsUsageFromAPI
    return 0;
  };

  const getRemainingTicketsCount = (booking: Booking) => {
    const totalTickets = (booking.freeTickets || 0) + (booking.additionalTickets || 0) + (booking.additionalTicketsCount || 0);
    const usedTickets = getUsedTicketsCount(booking);
    return Math.max(0, totalTickets - usedTickets);
  };

  const getLastScanInfo = (booking: Booking) => {
    // يمكن إضافة هذه المعلومة من الـ backend لاحقاً
    return null;
  };

  const loadTickets = async (bookingId: string) => {
    try {
      setLoadingTickets(true);
      
      // جلب التذاكر الأصلية
      const response = await ticketService.getTicketsByBooking(bookingId);
      const originalTickets = response.tickets || [];
      
      // جلب التذاكر الإضافية من الحجز
      const { bookingService } = await import('../services/BookingService');
      const booking = await bookingService.getBookingById(bookingId);
      const additionalTickets = booking?.additionalTicketsPurchased || [];
      
      // دمج التذاكر الأصلية مع الإضافية
      const allTickets = [
        ...originalTickets.map((ticket: any) => ({
          ...ticket,
          type: 'original'
        })),
        ...additionalTickets.map((ticket: any) => ({
          id: `additional_${ticket.ticketNumber}`,
          ticketNumber: ticket.ticketNumber,
          qrCode: ticket.qrCode,
          used: ticket.used,
          usedAt: ticket.usedAt,
          type: 'additional',
          purchaseDate: ticket.purchaseDate
        }))
      ];
      
      setTickets(allTickets);
      
      // حساب عدد التذاكر المستخدمة (الأصلية والإضافية)
      const usedCount = allTickets.filter((t: any) => t.used).length;
      
      // تحديث كاش الاستخدام
      setTicketsUsageCache(prev => ({...prev, [bookingId]: usedCount}));
      
    } catch (err) {
      console.error('Error loading tickets:', err);
      setTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleShowScanDetails = async (booking: Booking) => {
    setShowScanDetails({ booking, show: true });
    if (booking.id || booking._id) {
      await loadTickets(booking.id || booking._id || '');
    }
  };

  const toggleDeleteMode = () => {
    setDeleteMode(!deleteMode);
    setSelectedForDelete([]);
  };

  const toggleSelectForDelete = (bookingId: string) => {
    if (selectedForDelete.includes(bookingId)) {
      setSelectedForDelete(selectedForDelete.filter(id => id !== bookingId));
    } else {
      setSelectedForDelete([...selectedForDelete, bookingId]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedForDelete.length === 0) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      // حذف كل حجز محدد
      for (const bookingId of selectedForDelete) {
        await bookingServiceAPI.deleteBooking(bookingId);
      }
      
      // إعادة تحميل البيانات
      await loadBookings();
      
      // إعادة تعيين الحالة
      setSelectedForDelete([]);
      setDeleteMode(false);
      setShowDeleteConfirm(false);
      
      alert(`تم حذف ${selectedForDelete.length} حجوزات بنجاح`);
    } catch (error) {
      console.error('Error deleting bookings:', error);
      alert('حدث خطأ في حذف الحجوزات');
    }
  };

  const generateInvoice = async (booking: Booking) => {
    try {
      const bookingId = booking.id || booking._id;
      if (!bookingId) {
        alert('خطأ: معرف الحجز غير موجود');
        return;
      }

      const baseUrl = process.env.REACT_APP_API_URL || 'https://fotomista-backend.vercel.app';
      
      const response = await fetch(`${baseUrl}/api/invoices/create/${bookingId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token') || 'temp-admin-token-123'}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Generate PDF for the invoice
        const pdfResponse = await fetch(`${baseUrl}/api/invoices/${data.invoice._id}/pdf`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('fotomista_token') || 'temp-admin-token-123'}`
          }
        });

        if (pdfResponse.ok) {
          // Open the invoice preview in a new tab
          window.open(`${baseUrl}/api/invoices/${data.invoice._id}/preview`, '_blank');
          alert('تم إنشاء الفاتورة بنجاح');
        } else {
          alert('تم إنشاء الفاتورة ولكن فشل في إنشاء ملف PDF');
        }
      } else {
        let errorMessage = 'خطأ غير معروف';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || 'خطأ غير معروف';
        } catch (e) {
          errorMessage = 'فشل في قراءة رسالة الخطأ';
        }
        alert('خطأ في إنشاء الفاتورة: ' + errorMessage);
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('خطأ في إنشاء الفاتورة');
    }
  };

  return (
    <motion.div 
      className="confirmed-bookings-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">
            <Link to="/">
              <img src="/images/logo.png" alt="Fotomista" />
            </Link>
          </div>
          <div className="page-title">
            العملاء المؤكدون
          </div>
          <div className="nav-actions">
            <Link to="/admin/bookings-management" className="back-to-admin">
              العودة لإدارة الحجوزات
            </Link>
            <button onClick={handleLogout} className="logout-btn">
              تسجيل خروج
            </button>
          </div>
        </div>
      </nav>

      <div className="bookings-container">
        <div className="bookings-header">
          <div className="stats-cards">
            <div className="stat-card">
              <h3>إجمالي الحجوزات</h3>
              <p className="stat-number">{bookings.length}</p>
            </div>
            <div className="stat-card">
              <h3>حجوزات اليوم</h3>
              <p className="stat-number">
                {bookings.filter(b => 
                  new Date(b.eventDate).toDateString() === new Date().toDateString()
                ).length}
              </p>
            </div>
            <div className="stat-card">
              <h3>إجمالي المبيعات</h3>
              <p className="stat-number">
                {bookings.reduce((sum, b) => sum + b.totalAmount, 0).toLocaleString('en-US')}
                <RiyalSymbol size={20} />
              </p>
            </div>
          </div>

          <div className="management-actions" style={{ marginTop: '20px', textAlign: 'center' }}>
            <button
              onClick={toggleDeleteMode}
              className={`delete-mode-btn ${deleteMode ? 'active' : ''}`}
              style={{
                background: deleteMode ? '#e74c3c' : '#95a5a6',
                color: 'white',
                border: 'none',
                padding: '10px 30px',
                borderRadius: '25px',
                fontSize: '16px',
                cursor: 'pointer',
                marginRight: '10px',
                transition: 'all 0.3s ease'
              }}
            >
              {deleteMode ? 'إلغاء وضع الحذف' : 'تفعيل وضع الحذف'}
            </button>
            
            {deleteMode && selectedForDelete.length > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="delete-selected-btn"
                style={{
                  background: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '10px 30px',
                  borderRadius: '25px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                حذف المحدد ({selectedForDelete.length})
              </button>
            )}
          </div>

          <div className="filters">
            <input
              type="text"
              placeholder="بحث بالاسم أو الهاتف أو رقم الحجز..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="date-filter"
            />
            {(searchTerm || filterDate) && (
              <button 
                className="clear-filters"
                onClick={() => {
                  setSearchTerm('');
                  setFilterDate('');
                }}
              >
                مسح التصفية
              </button>
            )}
          </div>
        </div>

        <div className="bookings-table-new">
          {loading ? (
            <div className="loading-container">
              <div className="loader"></div>
              <p>جاري تحميل الحجوزات...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <p className="error-message">{error}</p>
              <button onClick={loadBookings} className="retry-btn">إعادة المحاولة</button>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="no-bookings">
              <p>لا توجد حجوزات مؤكدة</p>
            </div>
          ) : (
            <div className="bookings-grid">
              {filteredBookings.map(booking => {
                const usedTickets = getUsedTicketsCount(booking);
                const remainingTickets = getRemainingTicketsCount(booking);
                
                const bookingId = booking.id || booking._id || '';
                const isSelected = selectedForDelete.includes(bookingId);
                
                return (
                  <motion.div 
                    key={booking.id}
                    className={`booking-card ${deleteMode ? 'delete-mode' : ''} ${isSelected ? 'selected-for-delete' : ''}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ y: -5 }}
                    style={{
                      border: isSelected ? '2px solid #e74c3c' : undefined,
                      backgroundColor: isSelected ? '#fee' : undefined
                    }}
                  >
                    <div className="card-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        {deleteMode && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectForDelete(bookingId)}
                            style={{
                              width: '20px',
                              height: '20px',
                              cursor: 'pointer',
                              marginTop: '5px'
                            }}
                          />
                        )}
                        <div>
                          <h3>{booking.customerName}</h3>
                          <span className="booking-code">#{booking.bookingCode}</span>
                        </div>
                      </div>
                      {booking.bookingType === 'perPhoto' && !deleteMode && (
                        <button 
                          className="qr-payment-btn"
                          onClick={() => setShowPaymentQR({ booking, show: true })}
                          title="رابط شراء تذاكر إضافية"
                        >
                          <span className="qr-icon">🔗</span>
                        </button>
                      )}
                    </div>
                    
                    <div className="card-info">
                      <div className="info-row">
                        <span className="label">التاريخ (هجري):</span>
                        <span className="value">{formatDate(booking.eventDate)}</span>
                      </div>
                      
                      <div className="info-row">
                        <span className="label">التاريخ (ميلادي):</span>
                        <span className="value">
                          {new Date(booking.eventDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      
                      <div className="info-row">
                        <span className="label">وقت بدء المناسبة:</span>
                        <span className="value">{booking.eventTime}</span>
                      </div>
                      
                      <div className="info-row">
                        <span className="label">الموقع:</span>
                        <span className="value">{booking.location}</span>
                      </div>
                      
                      <div className="info-row">
                        <span className="label">نوع الحجز:</span>
                        <span className="value">{getBookingTypeLabel(booking.bookingType)}</span>
                      </div>
                      
                      <div className="info-row">
                        <span className="label">نوع البوث:</span>
                        <span className="value">
                          {booking.boothDisplayName || 'نوع البوث غير محدد'}
                        </span>
                      </div>
                      
                      {booking.bookingType === 'perPhoto' && (
                        <>
                          <div className="info-row">
                            <span className="label">التذاكر المجانية:</span>
                            <span className="value">{booking.freeTickets || booking.totalTickets || 0}</span>
                          </div>
                          <div className="info-row">
                            <span className="label">التذاكر الإضافية:</span>
                            <span className="value">
                              {(booking.additionalTickets || 0) + (booking.additionalTicketsCount || 0)}
                              {(booking.additionalTicketsCount || 0) > 0 && (
                                <span style={{ color: '#4caf50', fontSize: '0.9em' }}>
                                  {' '}(+{booking.additionalTicketsCount} مشتراة بعد الحجز)
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="label">حالة الاستخدام:</span>
                            <span className="value">
                              <span style={{ color: '#f44336' }}>{usedTickets}</span>
                              {' / '}
                              <span style={{ color: '#4caf50' }}>{(booking.freeTickets || 0) + (booking.additionalTickets || 0) + (booking.additionalTicketsCount || 0)}</span>
                              <span style={{ color: '#666', fontSize: '0.9em' }}>
                                {' '}(متبقي: {remainingTickets})
                              </span>
                            </span>
                          </div>
                        </>
                      )}
                      
                      {booking.bookingType === 'hourly' && (
                        <div className="info-row">
                          <span className="label">عدد الساعات:</span>
                          <span className="value">{booking.hours} ساعات</span>
                        </div>
                      )}
                      
                      <div className="info-row">
                        <span className="label">حالة الإيميل:</span>
                        <span className="value" style={{ 
                          color: booking.emailSent ? '#4caf50' : '#ff9800',
                          fontWeight: 'bold' 
                        }}>
                          {booking.emailSent ? '✅ تم الإرسال' : '⏳ لم يتم الإرسال'}
                        </span>
                      </div>
                      
                      <div className="info-row">
                        <span className="label">حالة التذاكر:</span>
                        <span className="value" style={{ 
                          color: booking.ticketsGenerated ? '#4caf50' : '#ff9800',
                          fontWeight: 'bold' 
                        }}>
                          {booking.ticketsGenerated ? '✅ تم إنشاؤها' : '⏳ لم تُنشأ بعد'}
                        </span>
                      </div>
                      
                      <div className="info-row">
                        <span className="label">إرسال التذاكر:</span>
                        <span className="value" style={{ 
                          color: booking.ticketsEmailSent ? '#4caf50' : '#ff9800',
                          fontWeight: 'bold' 
                        }}>
                          {booking.ticketsEmailSent ? '✅ تم الإرسال' : '⏳ لم يتم الإرسال'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="card-actions">
                      <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                        <button
                          onClick={() => generateInvoice(booking)}
                          className="action-btn invoice-btn"
                          style={{ backgroundColor: '#ff6b35', color: 'white' }}
                        >
                          🧾 استخراج فاتورة
                        </button>
                        <button
                          onClick={() => {
                            const bookingId = booking.id || booking._id;
                            window.open(`${process.env.REACT_APP_API_URL || 'https://fotomista-backend-production.up.railway.app'}/api/invoices/booking/${bookingId}/preview`, '_blank');
                          }}
                          className="action-btn"
                          style={{ backgroundColor: '#2196F3', color: 'white', fontSize: '12px' }}
                        >
                          👁️ معاينة الفاتورة
                        </button>
                      </div>
                      {booking.ticketsGenerated && booking.bookingType === 'perPhoto' && (
                        <a 
                          href={`/tickets/${booking.bookingCode}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-btn tickets-btn"
                          style={{ backgroundColor: '#4caf50' }}
                        >
                          🎫 عرض التذاكر
                        </a>
                      )}
                      
                      {booking.bookingType === 'perPhoto' && (
                        <button 
                          className="action-btn scan-btn"
                          onClick={() => handleShowScanDetails(booking)}
                        >
                          📊 تفاصيل المسح
                        </button>
                      )}
                      
                      <a 
                        href={`/pass/${booking.bookingCode}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="action-btn pass-btn"
                      >
                        🎟️ صفحة Pass
                      </a>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal تأكيد الحذف */}
      {showDeleteConfirm && (
        <div className="delete-confirm-modal" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <motion.div 
            className="confirm-content"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
            style={{
              background: 'white',
              borderRadius: '15px',
              padding: '40px',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
            }}
          >
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>⚠️</div>
            <h2 style={{ color: '#e74c3c', marginBottom: '20px' }}>تأكيد الحذف</h2>
            <p style={{ fontSize: '18px', marginBottom: '20px' }}>
              هل أنت متأكد من حذف {selectedForDelete.length} حجز؟
            </p>
            <p style={{ color: '#666', marginBottom: '30px' }}>
              هذا الإجراء لا يمكن التراجع عنه وسيتم حذف جميع البيانات المتعلقة بهذه الحجوزات نهائياً.
            </p>
            
            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>الحجوزات المحددة للحذف:</p>
              <div style={{ 
                maxHeight: '150px', 
                overflowY: 'auto', 
                background: '#f8f9fa', 
                padding: '10px', 
                borderRadius: '8px',
                textAlign: 'right'
              }}>
                {selectedForDelete.map(id => {
                  const booking = bookings.find(b => (b.id || b._id) === id);
                  return booking ? (
                    <div key={id} style={{ padding: '5px 0', borderBottom: '1px solid #e0e0e0' }}>
                      {booking.customerName} - #{booking.bookingCode}
                    </div>
                  ) : null;
                })}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={confirmDelete}
                style={{
                  background: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '12px 40px',
                  borderRadius: '25px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                نعم، احذف
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  background: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 40px',
                  borderRadius: '25px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                إلغاء
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal لعرض تفاصيل المسح */}
      {showScanDetails?.show && (
        <div className="scan-details-modal" onClick={() => setShowScanDetails(null)}>
          <motion.div 
            className="modal-content"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>تفاصيل مسح التذاكر</h3>
              <button 
                className="close-btn"
                onClick={() => setShowScanDetails(null)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <div className="booking-info">
                <p><strong>رقم الحجز:</strong> {showScanDetails.booking.bookingCode}</p>
                <p><strong>اسم العميل:</strong> {showScanDetails.booking.customerName}</p>
                <p><strong>إجمالي التذاكر:</strong> {showScanDetails.booking.totalTickets}</p>
              </div>
              
              <div className="tickets-grid">
                {loadingTickets ? (
                  <div className="loading-tickets">جاري تحميل التذاكر...</div>
                ) : tickets.map((ticket, index) => (
                  <div 
                    key={ticket.id} 
                    className={`ticket-card ${ticket.used ? 'used' : 'unused'} ${ticket.type === 'additional' ? 'additional-ticket' : ''}`}
                  >
                    <div className="ticket-number">
                      تذكرة {ticket.ticketNumber}
                      {ticket.type === 'additional' && (
                        <span className="additional-badge">إضافية</span>
                      )}
                    </div>
                    <div className="ticket-status">
                      {ticket.used ? (
                        <>
                          <span className="status-icon">✓</span>
                          <span className="status-text">مستخدمة</span>
                          <div className="scan-time">
                            {new Date(ticket.usedAt!).toLocaleString('ar-SA', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit'
                            })}
                          </div>
                        </>
                      ) : (
                        <>
                          <span className="status-icon">✗</span>
                          <span className="status-text">غير مستخدمة</span>
                          {ticket.type === 'additional' && ticket.purchaseDate && (
                            <div className="purchase-date">
                              اشتريت في: {new Date(ticket.purchaseDate).toLocaleDateString('ar-SA')}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="summary-stats">
                <div className="stat">
                  <span className="stat-label">التذاكر المستخدمة:</span>
                  <span className="stat-value used">{tickets.filter(t => t.used).length}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">التذاكر المتبقية:</span>
                  <span className="stat-value remaining">{tickets.filter(t => !t.used).length}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">التذاكر الأصلية:</span>
                  <span className="stat-value original">{tickets.filter(t => t.type === 'original').length}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">التذاكر الإضافية:</span>
                  <span className="stat-value additional">{tickets.filter(t => t.type === 'additional').length}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal لعرض QR code رابط الدفع */}
      {showPaymentQR?.show && (() => {
        const paymentUrl = `${window.location.origin}/additional-payment/${showPaymentQR.booking.id}`;
        
        return (
          <div className="payment-qr-modal" onClick={() => setShowPaymentQR(null)}>
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>رابط شراء تذاكر إضافية</h3>
                <button 
                  className="close-btn"
                  onClick={() => setShowPaymentQR(null)}
                >
                  ✕
                </button>
              </div>
              
              <div className="modal-body">
                <div className="qr-container">
                  <QRCode 
                    value={paymentUrl}
                    size={256}
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    viewBox={`0 0 256 256`}
                  />
                </div>
                
                <div className="payment-info">
                  <p className="info-text">امسح هذا الرمز للوصول لصفحة شراء تذاكر إضافية</p>
                  <div className="url-section">
                    <input 
                      type="text" 
                      value={paymentUrl} 
                      readOnly 
                      className="url-input"
                    />
                    <button 
                      className="copy-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(paymentUrl);
                        alert('تم نسخ الرابط');
                      }}
                    >
                      نسخ
                    </button>
                  </div>
                  <a 
                    href={paymentUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="direct-link"
                  >
                    فتح الرابط
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        );
      })()}
    </motion.div>
  );
}

export default ConfirmedBookings;